import type { $Enums } from '@prisma/client';
import { definePolicy, type Policy } from '../kernel';
import { allow, type Decision, deny } from '../types';

/**
 * DOCUMENT ONBOARDING — a new document layout the onboarding agent drafted, reviewed and saved by a user.
 *
 *   DRAFTING ─┬─► READY ──save──► MAPPING ─┬─► READING ─┬─► DONE
 *             ├─► UNSUPPORTED              └─► MAP_FAILED └─► READ_FAILED
 *             └─► DRAFT_FAILED                 (each failure ──retry──► its step)
 *
 * The picks change while the draft waits for review (READY), and again after its mapper failed (MAP_FAILED), so the
 * retry maps the layout as the user now has it. `save` hands a READY layout on; `retry` runs a failed step again.
 */
export type DocumentOnboardingStatus = $Enums.DocumentOnboardingStatus;

export type DocumentOnboardingFacts = { status: DocumentOnboardingStatus };

export const DOCUMENT_ONBOARDING_FIELDS = ['picks'] as const;
export type DocumentOnboardingField = (typeof DOCUMENT_ONBOARDING_FIELDS)[number];

export const DOCUMENT_ONBOARDING_OPS = ['save', 'retry'] as const;
export type DocumentOnboardingOp = (typeof DOCUMENT_ONBOARDING_OPS)[number];

const PICKS_EDITABLE: ReadonlySet<DocumentOnboardingStatus> = new Set(['READY', 'MAP_FAILED']);

/** Each failure and the step its retry runs again. */
export const DOCUMENT_ONBOARDING_RETRY_STEP: Readonly<
  Partial<Record<DocumentOnboardingStatus, DocumentOnboardingStatus>>
> = { DRAFT_FAILED: 'DRAFTING', MAP_FAILED: 'MAPPING', READ_FAILED: 'READING' };

/** Why the layout is not open to review, by where it stands. */
const NOT_IN_REVIEW: Readonly<Record<DocumentOnboardingStatus, string>> = {
  DRAFTING: 'The layout is still being drafted.',
  READY: 'The layout is waiting for review.',
  UNSUPPORTED: 'A scanned document has no layout to review.',
  DRAFT_FAILED: 'The layout has no draft to review yet.',
  MAPPING: 'The layout is saved and being set up.',
  MAP_FAILED: 'The layout is saved and waiting to be set up again.',
  READING: 'The layout is saved and the document is being read with it.',
  READ_FAILED: 'The layout is saved and waiting to read the document again.',
  DONE: 'The layout is saved and the document is read.',
};

function decidePicks(status: DocumentOnboardingStatus): Decision {
  return PICKS_EDITABLE.has(status) ? allow() : deny('DOCUMENT_ONBOARDING_PICKS_LOCKED', NOT_IN_REVIEW[status]);
}

function decideOp(status: DocumentOnboardingStatus, op: DocumentOnboardingOp): Decision {
  if (op === 'save') return status === 'READY' ? allow() : deny('DOCUMENT_ONBOARDING_NOT_READY', NOT_IN_REVIEW[status]);
  return DOCUMENT_ONBOARDING_RETRY_STEP[status]
    ? allow()
    : deny('DOCUMENT_ONBOARDING_NOTHING_TO_RETRY', 'Only a step that went wrong is tried again.');
}

export const documentOnboardingPolicy: Policy<DocumentOnboardingFacts, DocumentOnboardingField, DocumentOnboardingOp> =
  definePolicy<DocumentOnboardingFacts, DocumentOnboardingField, DocumentOnboardingOp>({
    kind: 'document layout',
    fields: DOCUMENT_ONBOARDING_FIELDS,
    ops: DOCUMENT_ONBOARDING_OPS,
    requires: {},
    entity: (facts, action) => {
      if (action.type === 'perform') return decideOp(facts.status, action.op);
      if (action.type === 'edit') return decidePicks(facts.status);
      return allow();
    },
  });
